import { useCallback } from "react";
import { match } from "ts-pattern";
import { useAgentRunMutations } from "@/hooks/agent/useAgentRunMutations";
import { useThreadCosts } from "@/hooks/agent/useThreadCosts";
import { useThreadMessageMutations } from "@/hooks/agent/useThreadMessageMutations";
import { ThreadEvent } from "@/models/agent/threadEvent";
import { getAgentThreadStore } from "@/stores/agent/agentThreadStore";

export const useThreadEventHandler = (
  threadId: string | undefined,
) => {
  const { mutate: mutateThreadCosts } = useThreadCosts(threadId || null);
  const { optimisticUpdate: optimisticUpdateAgentRun } = useAgentRunMutations(threadId || null);
  const { pushOptimisticMessage } = useThreadMessageMutations(threadId);
  const setLatestAgentStatus = getAgentThreadStore(threadId)((state) => state.setLatestAgentStatus);

  const onThreadEvent = useCallback((event: ThreadEvent) => {
    match(event)
      .with({ type: "message" }, ({ data: message }) => {
        match(message.role)
          .with("assistant", "assistant_response_end", "tool_call", "tool_response", () => {
            if (message.isSubAgentFinalResponse) return;

            pushOptimisticMessage(message);
            mutateThreadCosts();
          })
          .with("user", () => {})
          .otherwise(() => {
            console.warn("Unknown message:", message);
          });
      })
      .with({ type: "agent_status" }, ({ data: agentStatus }) => {
        setLatestAgentStatus?.(agentStatus);
      })
      .with({ type: "agent_run" }, ({ data: agentRun }) => {
        optimisticUpdateAgentRun(agentRun);

        if (!agentRun.isRunning) {
          setLatestAgentStatus?.(null);
        }
      })
      .exhaustive();
  }, [pushOptimisticMessage, setLatestAgentStatus, mutateThreadCosts, optimisticUpdateAgentRun]);

  return { onThreadEvent };
};
